"use client";

import { motion } from "framer-motion";
import { Fragment, useMemo, useState, type CSSProperties } from "react";
import { Download, ShoppingCart, Send, Check } from "lucide-react";
import { toast } from "sonner";
import type { Category, Product } from "@/lib/products";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCart } from "@/context/CartContext";

const viewLabels = ["Front", "Working", "Cab", "Boom", "Rear"];

const downloadBrochure = (product: Product) => {
  if (!product.brochureUrl) {
    toast.error("Brochure is not available yet");
    return;
  }

  const a = document.createElement("a");
  a.href = product.brochureUrl;
  a.download = `${product.name.replace(/\s+/g, "-")}-brochure.pdf`;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.click();
  toast.success("Brochure downloaded");
};

const ProductDetailView = ({
  slug,
  product,
  category,
}: {
  slug: string;
  product: Product;
  category: Category;
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const { addToCart } = useCart();
  const whatsappUrl = product.whatsappUrl || product.whatsappLink;
  const supportTitle = Array.isArray(product.support) ? "Support & Service" : product.support?.title || "Support & Service";
  const supportDescription = Array.isArray(product.support) ? undefined : product.support?.description;
  const supportItems = (Array.isArray(product.support) ? product.support : product.support?.items) || [
    { t: "OEM Parts", d: "Genuine Engirise parts, in stock and ready to ship." },
    { t: "Field Service", d: "On-site repair and scheduled maintenance." },
    { t: "Operator Training", d: "Certified training for crews on every machine class." },
  ];
  const applicationTitle = typeof product.applications === "object" && !Array.isArray(product.applications)
    ? product.applications.title || "Applications"
    : "Applications";
  const applicationDescription = typeof product.applications === "object" && !Array.isArray(product.applications)
    ? product.applications.description
    : undefined;
  const applicationItems = typeof product.applications === "object" && !Array.isArray(product.applications)
    ? product.applications.items || []
    : Array.isArray(product.applications)
      ? product.applications
      : [product.applications];

  const galleryImages = useMemo(
    () => {
      if (!product) return [];
      const images = product.imageGallery && product.imageGallery.length > 0
        ? product.imageGallery
        : [product.image?.url || ""];
      return images.slice(0, 5).map((src, index) => ({
        src,
        alt: `${product.name} ${viewLabels[index] || "View"}`,
        label: viewLabels[index] || `View ${index + 1}`,
        position: "center",
      }));
    },
    [product],
  );

  const handleAdd = () => {
    addToCart({
      id: product.id,
      name: product.name,
      image: product.image?.url || "",
      subcategory: "", // Will be populated from sub-category context
      category: category.name,
      categorySlug: category.slug,
      model: product.model,
    });
    toast.success(`${product.name} added to cart`);
  };

  return (
    <>
      {/* Hero */}
      <section className="py-10 sm:py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
            {/* Gallery */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <div className="relative aspect-[4/3] bg-muted border border-border rounded-md overflow-hidden">
                <img
                  src={galleryImages[activeImageIndex].src}
                  alt={galleryImages[activeImageIndex].alt}
                  className="w-full h-full object-fit"
                  style={{ objectPosition: galleryImages[activeImageIndex].position as CSSProperties["objectPosition"] }}
                />
                <span className="absolute top-4 left-4 bg-card border border-border px-3 py-1 text-sm font-bold uppercase tracking-wider rounded-sm">
                  {galleryImages[activeImageIndex].label}
                </span>
              </div>
              <div className="grid grid-cols-5 gap-2 mt-3">
                {galleryImages.map((img, i) => (
                  <button
                    key={img.label}
                    onClick={() => setActiveImageIndex(i)}
                    className={`aspect-square overflow-hidden rounded-sm border-2 transition ${
                      activeImageIndex === i ? "border-primary" : "border-border hover:border-primary/50"
                    }`}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-full object-fit"
                      style={{ objectPosition: img.position as CSSProperties["objectPosition"] }}
                    />
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Info */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                <span className="text-primary font-bold">{category.name}</span>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-foreground mt-3 uppercase leading-[0.95] break-words">{product.name}</h1>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                {product.description}
              </p>

              {/* Key specs */}
              <div className="mt-6 grid grid-cols-2 gap-px bg-border border border-border rounded-md overflow-hidden">
                {product.stats?.slice(0, 4).map((stat) => (
                  <div key={stat.label} className="bg-card p-4 min-w-0">
                    <p className="text-sm uppercase tracking-wider text-muted-foreground">
                      {stat.label}
                    </p>
                    <p className="mt-1 font-display text-base sm:text-lg text-foreground break-words">
                      {stat.value} {stat.unit}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAdd}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 rounded-sm font-bold text-sm uppercase tracking-wider hover:-translate-y-0.5 transition"
                >
                  <ShoppingCart className="w-4 h-4" /> Add to Cart
                </button>
                <a
                  href={whatsappUrl || "#inquiry"}
                  className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-foreground text-foreground px-6 py-3.5 rounded-sm font-bold text-sm uppercase tracking-wider hover:bg-foreground hover:text-background transition"
                >
                  <Send className="w-4 h-4" /> {whatsappUrl ? "WhatsApp" : "Send Inquiry"}
                </a>
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => downloadBrochure(product)}
                    className="mt-3 w-full inline-flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-muted border border-border px-5 py-3 rounded-sm text-sm font-semibold hover:border-primary transition text-left"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Download className="w-4 h-4 text-primary" />
                      Download Brochure
                    </span>
                    <span className="text-xs text-muted-foreground">PDF · 2.4 MB</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>Download detailed specs</TooltipContent>
              </Tooltip>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-10 sm:py-12 border-t border-border bg-muted/30">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="specifications" className="w-full">
            <TabsList className="bg-card border border-border rounded-sm h-auto p-1 mb-6 flex flex-wrap">
              {["specifications", "applications", "support"].map((t) => (
                <TabsTrigger
                  key={t}
                  value={t}
                  className="px-5 py-2.5 text-sm font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  {t}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="specifications" className="mt-0">
              <div className="bg-card border border-border rounded-md p-5 sm:p-7 md:p-10">
                <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-3xl sm:text-4xl uppercase">Technical Specifications</h2>
              </div>
                <div className="mt-6 overflow-x-auto">
                <table className="w-full min-w-[520px] border border-border">
                  <tbody>
                    {Object.entries(product.specs || {}).map(([sectionTitle, specs]) => (
                      <Fragment key={sectionTitle}>
                        <tr>
                          <td colSpan={2} className="bg-primary text-primary-foreground px-5 py-3 text-sm font-bold uppercase tracking-wider">
                            {sectionTitle}
                          </td>
                        </tr>

                        {specs.map((spec, i) => (
                          <tr key={`${sectionTitle}-${spec.l}`} className={i % 2 === 0 ? "bg-card" : "bg-muted/40"}>
                            <td className="px-5 py-4 text-sm text-muted-foreground border-b border-border w-1/2">
                              {spec.l}
                            </td>
                            <td className="px-5 py-4 text-sm font-bold text-foreground border-b border-border">
                              {spec.v}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="applications" className="mt-0">
              <div className="bg-card border border-border rounded-md p-5 sm:p-7 md:p-10">
                <h2 className="font-display text-3xl sm:text-4xl uppercase">{applicationTitle}</h2>
                {applicationDescription ? (
                  <p className="mt-4 text-muted-foreground leading-relaxed">{applicationDescription}</p>
                ) : null}
                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {applicationItems.filter(Boolean).map((app) => {
                    const appTitle = typeof app === "string" ? app : app.title;
                    const appDescription = typeof app === "string" ? undefined : app.description;

                    return (
                    <div key={appTitle} className="border border-border bg-muted/30 p-5 rounded-sm">
                      <Check className="w-5 h-5 text-primary" />
                      <p className="mt-3 font-display text-lg uppercase">{appTitle}</p>
                      {appDescription ? (
                        <p className="mt-2 text-sm text-muted-foreground">{appDescription}</p>
                      ) : null}
                    </div>
                  );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="support" className="mt-0">
              <div className="bg-card border border-border rounded-md p-5 sm:p-7 md:p-10">
                <h2 className="font-display text-3xl sm:text-4xl uppercase">{supportTitle}</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  {supportDescription || `Kasai supports every ${product.name} with factory-trained technicians, OEM parts inventory, and 24/7 emergency response across the region.`}
                </p>
                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {supportItems.map((s) => (
                    <div key={s.title || s.t} className="border border-border p-5 rounded-sm">
                      <p className="font-display text-lg uppercase">{s.title || s.t}</p>
                      <p className="mt-2 text-sm text-muted-foreground">{s.description || s.d}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="inquiry" className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl text-foreground uppercase text-center">Send Inquiry</h2>
          <p className="text-center text-muted-foreground mt-3">
            Interested in the {product.name}? Our team will respond within 24 hours.
          </p>

          {submitted ? (
            <div className="mt-8 bg-card border border-border rounded-md p-10 text-center">
              <Check className="w-12 h-12 text-primary mx-auto" />
              <h3 className="font-display text-base mt-4 uppercase">Inquiry Sent</h3>
              <p className="text-muted-foreground mt-2 text-sm">We'll get back to you shortly.</p>
              <button onClick={() => setSubmitted(false)} className="mt-5 text-primary text-sm font-semibold hover:underline">
                Send another →
              </button>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitted(true);
              }}
              className="mt-8 bg-card border border-border rounded-md p-5 sm:p-7 space-y-4"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Name *" className="w-full border border-border bg-background px-4 py-3 rounded-sm text-sm focus:outline-none focus:border-primary" />
                <input required type="email" placeholder="Email *" className="w-full border border-border bg-background px-4 py-3 rounded-sm text-sm focus:outline-none focus:border-primary" />
              </div>
              <input type="tel" placeholder="Phone" className="w-full border border-border bg-background px-4 py-3 rounded-sm text-sm focus:outline-none focus:border-primary" />
              <textarea required rows={4} placeholder="Message" className="w-full border border-border bg-background px-4 py-3 rounded-sm text-sm focus:outline-none focus:border-primary resize-none" />
              <button type="submit" className="w-full bg-primary text-primary-foreground py-3.5 rounded-sm font-bold text-sm uppercase tracking-wider hover:-translate-y-0.5 transition">
                Submit Inquiry
              </button>
            </form>
          )}
        </div>
      </section>

    </>
  );
};

export default ProductDetailView;
